import React, { useState } from 'react';
import Button from '@/components/ui/custom/Button';
import Icon from '@/components/AppIcon';
import { formatProposalDeadline } from '@/contexts/assistantProposals';
import { getOrgRoleLabel } from '@/utils/roleStyles';

// A change the assistant proposed, for the coach to confirm: a new poll, a
// roster change or a saved location. The assistant cannot make any of these;
// Confirm runs the app's own code for it (domains/assistant/proposalExecutors).

const VENUE_TYPE_LABELS = { main: 'Main Facility', practice: 'Practice Facility', hotel: 'Hotel', other: 'Other' };
const VENUE_CONTEXT_LABELS = { home: 'Home', away: 'Away', neutral: 'Neutral' };

const HEADINGS = {
  poll: { icon: 'Vote', title: 'New poll', confirm: 'Create poll' },
  roster_add: { icon: 'UserPlus', title: 'Add to roster', confirm: 'Add member' },
  roster_update: { icon: 'UserRound', title: 'Roster change', confirm: 'Save change' },
  saved_location: { icon: 'MapPin', title: 'New saved location', confirm: 'Save location' },
};

const Row = ({ label, children }) => (
  <div className="flex gap-2 text-sm">
    <dt className="w-20 flex-shrink-0 text-muted-foreground">{label}</dt>
    <dd className="min-w-0 flex-1 break-words text-foreground">{children}</dd>
  </div>
);

const PollDetails = ({ proposal }) => (
  <>
    <p className="text-sm font-medium text-foreground">{proposal.title}</p>
    {proposal.description && <p className="text-xs text-muted-foreground">{proposal.description}</p>}
    <ul className="mt-2 list-disc space-y-0.5 pl-5 text-sm text-foreground">
      {(proposal.options || []).map((option) => (
        <li key={option.label}>
          {option.label}
          {option.cuisineType ? <span className="text-xs text-muted-foreground"> · {option.cuisineType}</span> : null}
        </li>
      ))}
    </ul>
    <p className="mt-2 text-xs text-muted-foreground">
      Voting closes {formatProposalDeadline(proposal.closes)}
      {proposal.allowMultiple ? ' · more than one choice allowed' : ''}
      {proposal.allowSuggestions ? ' · members can add options' : ''}
      {proposal.anonymous ? ' · anonymous' : ''}
    </p>
  </>
);

const RosterAddDetails = ({ proposal, orgType }) => (
  <dl className="space-y-1">
    <Row label="Name">{proposal.member?.full_name}</Row>
    <Row label="Role">{getOrgRoleLabel(proposal.member?.role, orgType)}</Row>
    <Row label="Email">{proposal.member?.email}</Row>
    <Row label="Allergies">{proposal.member?.allergies || 'None'}</Row>
  </dl>
);

const Change = ({ from, to }) => (
  <>
    <span className="text-muted-foreground line-through">{from}</span>
    <span className="px-1 text-muted-foreground">→</span>
    <span className="font-medium">{to}</span>
  </>
);

const RosterUpdateDetails = ({ proposal, orgType }) => {
  const { before = {}, changes = {} } = proposal;
  return (
    <>
      <p className="text-sm font-medium text-foreground">{before.full_name}</p>
      <dl className="mt-2 space-y-1">
        {changes.full_name != null && (
          <Row label="Name">
            <Change from={before.full_name} to={changes.full_name} />
          </Row>
        )}
        {changes.role != null && (
          <Row label="Role">
            <Change from={getOrgRoleLabel(before.role, orgType)} to={getOrgRoleLabel(changes.role, orgType)} />
          </Row>
        )}
        {changes.allergies != null && (
          <Row label="Allergies">
            <Change from={before.allergies || 'None'} to={changes.allergies || 'None'} />
          </Row>
        )}
      </dl>
    </>
  );
};

const SavedLocationDetails = ({ proposal, entryId, choice, onChoose, locked }) => {
  const candidates = proposal.candidates || [];
  return (
    <>
      <p className="text-sm font-medium text-foreground">{proposal.addressName}</p>
      <p className="text-xs text-muted-foreground">
        {VENUE_TYPE_LABELS[proposal.venueType] || proposal.venueType} · {VENUE_CONTEXT_LABELS[proposal.venueContext] || proposal.venueContext}
      </p>
      {candidates.length === 1 ? (
        <p className="mt-2 text-sm text-foreground">{candidates[0].description}</p>
      ) : (
        <fieldset className="mt-2 space-y-1.5" disabled={locked}>
          <legend className="text-xs text-muted-foreground">Pick the right address:</legend>
          {candidates.map((candidate) => (
            <label key={candidate.placeId} className="flex cursor-pointer items-start gap-2 text-sm text-foreground">
              <input
                type="radio"
                name={`proposal-address-${entryId}`}
                className="mt-1"
                checked={choice === candidate.placeId}
                onChange={() => onChoose(candidate.placeId)}
              />
              <span>{candidate.description}</span>
            </label>
          ))}
        </fieldset>
      )}
      {(proposal.unit || proposal.deliveryNotes) && (
        <dl className="mt-2 space-y-1">
          {proposal.unit && <Row label="Unit">{proposal.unit}</Row>}
          {proposal.deliveryNotes && <Row label="Notes">{proposal.deliveryNotes}</Row>}
        </dl>
      )}
    </>
  );
};

const ProposalCard = ({ entry, orgType = null, onConfirm, onCancel, onOpen }) => {
  const proposal = entry?.proposal || {};
  const heading = HEADINGS[proposal.kind];
  const candidates = proposal.kind === 'saved_location' ? proposal.candidates || [] : [];
  const [choice, setChoice] = useState(candidates.length === 1 ? candidates[0].placeId : null);
  if (!heading) return null;

  const state = entry.state || 'pending';
  const working = state === 'working';
  const open = state === 'pending' || working || state === 'failed';
  const needsChoice = proposal.kind === 'saved_location' && !choice;

  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm" data-proposal-kind={proposal.kind} data-proposal-state={state}>
      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
        <Icon name={heading.icon} size={16} />
        <span>{heading.title}</span>
        {proposal.team_name && <span className="text-xs font-normal text-muted-foreground">· {proposal.team_name}</span>}
      </div>

      <div className="mt-3">
        {proposal.kind === 'poll' && <PollDetails proposal={proposal} />}
        {proposal.kind === 'roster_add' && <RosterAddDetails proposal={proposal} orgType={orgType} />}
        {proposal.kind === 'roster_update' && <RosterUpdateDetails proposal={proposal} orgType={orgType} />}
        {proposal.kind === 'saved_location' && (
          <SavedLocationDetails proposal={proposal} entryId={entry.id} choice={choice} onChoose={setChoice} locked={!open || working} />
        )}
      </div>

      {state === 'failed' && entry.error && (
        <p className="mt-3 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-sm text-error">{entry.error}</p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-2">
        {open ? (
          <>
            <Button size="sm" iconName="Check" loading={working} disabled={working || needsChoice} onClick={() => onConfirm(choice)}>
              {state === 'failed' ? 'Try again' : heading.confirm}
            </Button>
            <Button size="sm" variant="outline" disabled={working} onClick={onCancel}>
              Cancel
            </Button>
            {state === 'pending' && <p className="w-full text-xs text-muted-foreground">Nothing changes until you confirm.</p>}
          </>
        ) : state === 'done' ? (
          <>
            <p className="flex items-center gap-1.5 text-sm text-foreground">
              <Icon name="CheckCircle" size={14} className="text-success" />
              {entry.resultLabel || 'Done'}
            </p>
            {entry.path && (
              <Button size="sm" variant="outline" onClick={() => onOpen(entry.path)}>
                {entry.pathLabel || 'Open'}
              </Button>
            )}
          </>
        ) : (
          <p className="text-sm text-muted-foreground">
            {entry.superseded ? 'Replaced by a newer card below. Nothing was changed.' : 'Cancelled. Nothing was changed.'}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProposalCard;
